import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, computed, ElementRef, HostListener } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../auth/services/auth.service';
import {
    selectCountUnreadableNotifications,
} from '../../../notification-center/store/selectors/profileNotifications.selector';
import {
    selectCurrentPlayerPaymentAccountBalanceValue,
} from '../../../wallet/store/selectors/wallet.selector';
import { SUPPORT_URL } from '../../constants';
import { NotificationsService } from '../../services/notifications.service';
import { openCreateTournamentSelectGameModalAction } from '../../store/actions/commonSettings.action';
import { AppState } from '../../store/appState';
import { MobileSideMenuComponent } from '../mobile-side-menu/mobile-side-menu.component';
import { AsyncPipe, NgOptimizedImage } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { EditUserProfileComponent } from '../edit-user-profile/edit-user-profile.component';
import { PERMISSION_NAMES } from 'src/app/permissions/models/enums/permission-names';
import { selectGeneralPermissions } from 'src/app/permissions/store/selectors/permissions.selector';
// import { LanguageSelectorComponent } from '../language-selector/language-selector.component';
import { SvgIconComponent } from 'angular-svg-icon';

export interface HeaderNavigation {
    key?: string;
    title: string;
    icon: string;
    url: string;
    useExact?: boolean;
    mobileOnly?: boolean;
    newTab?: boolean;
    badge?: number | string | null;
}

@Component({

    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MobileSideMenuComponent,
        RouterLink,
        RouterLinkActive,
        TranslatePipe,
        AsyncPipe,
        EditUserProfileComponent,
        SvgIconComponent,
    ],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private store = inject<Store<AppState>>(Store);
    private router = inject(Router);
    private authService = inject(AuthService);
    private notificationsService = inject(NotificationsService);
    private el = inject(ElementRef);

    canBuyEarlyAccess = false;
    hasEarlySubscription = false;
    userPermissions = this.store.selectSignal(selectGeneralPermissions);

    navigation: HeaderNavigation[] = [
        {
            title: 'home',
            icon: 'assets/img/icon/navigation/home.svg',
            url: '/',
            useExact: true,
        },
        {
            title: 'tournaments',
            icon: 'assets/img/icon/navigation/tournaments.svg',
            url: '/all-tournaments',
            useExact: false,
        },
        {
            title: 'leaderboard',
            icon: 'assets/img/icon/navigation/leaderboard.svg',
            url: '/leaderboard',
            useExact: true,
        },
        {
            title: 'games',
            icon: 'assets/img/icon/navigation/games.svg',
            url: '/games',
            useExact: true,
        },
        {
            title: 'BGMI',
            icon: 'assets/img/icon/navigation/games.svg',
            url: '/games/BGMI',
            useExact: true,

        },{
            title: 'Rewards',
            icon: 'assets/img/icon/navigation/rewards.svg',
            url: '/rewards',
            useExact: true,
        },
        {
            title: 'support',
            icon: 'assets/img/icon/support.png',
            url: SUPPORT_URL,
            newTab: true,
            mobileOnly: true,
        },
    ];

    countUnreadableUserNotifications$: Observable<number> = this.store.select(
        selectCountUnreadableNotifications
    );

    permissions$: Observable<PERMISSION_NAMES[]> = this.store.select(
        selectGeneralPermissions
    );
    // TODO: ADD LIDOMA POINT GETTER
    getCurrentPlayerBalance$ = this.store.select(selectCurrentPlayerPaymentAccountBalanceValue);



    private destroy$ = new Subject<void>();

    get isPlayer() {
        return this.authService.isPlayer;
    }

    get isAuthorised() {
        return this.authService.isAuthorised;
    }

    canCreateTournament = false;
    canCreateLidomaTournament = false;

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    logIn() {
        this.authService.login();
    }

    openCreateTournamentModal() {
        if (!this.isAuthorised) {
            this.notificationsService.showShouldLoginMessage();
            return;
        }

        this.store.dispatch(openCreateTournamentSelectGameModalAction());
    }

    openNotifications() {
        this.router.navigateByUrl('/notification-center');
    }

    constructor() {
        computed(()=> {
            if (this.userPermissions()?.includes(PERMISSION_NAMES.SUBSCRIPTIONS_PURCHASE_EARLY_PASS)) {
                this.canBuyEarlyAccess = true;
            }
        });
    }

    ngOnInit(): void {
        this.permissions$.pipe(takeUntil(this.destroy$)).subscribe((permissions: PERMISSION_NAMES[]) => {
            this.canCreateLidomaTournament = permissions.includes(PERMISSION_NAMES.TOURNAMENT_CREATE_NEW);
            this.canCreateTournament = permissions.includes(PERMISSION_NAMES.TOURNAMENT_CREATE_NEW_CUSTOM);
            this.hasEarlySubscription = permissions.includes(PERMISSION_NAMES.SUBSCRIPTIONS_PURCHASE_EARLY_PASS) && ((permissions.includes(PERMISSION_NAMES.TOURNAMENT_JOIN_EARLY_ACCESS_SOLO) ||  permissions.includes(PERMISSION_NAMES.TOURNAMENT_JOIN_EARLY_ACCESS_TEAM))
                || (window.location.href.includes('all-tournaments') || window.location.href.includes('tournaments')))
        })
        computed(()=> {
            if (this.userPermissions()?.includes(PERMISSION_NAMES.SUBSCRIPTIONS_PURCHASE_EARLY_PASS)) {
                this.canBuyEarlyAccess = true;
            }
        });
    }


    @HostListener('window:scroll', [])
    onScroll() {
        const headerInner = this.el.nativeElement.querySelector('.header__inner');
        if (window.scrollY > 50) {
            headerInner.classList.add('scrolled');
        } else {
            headerInner.classList.remove('scrolled');
        }
    }
}
